import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";

import { GET, POST, PUT, DELETE } from "../../api/requests";
import { JSONEditor } from "./JSONEditor";
import { SimpleModal } from "../../components/Modals/SimpleModal";
import Button from "../../components/Button";

export default function ManageLambda() {
  const { lambdaId } = useParams();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState("");

  const [funcName, setFuncName] = useState("");
  const [config, setConfig] = useState({ description: "" });
  const [displayTitle, setDisplayTitle] = useState("");
  const [code, setCode] = useState("// Lambda function code");

  const [layers, setLayers] = useState([]);

  const { state } = useLocation();
  const [lambda, setLambda] = useState(state?.lambda);

  // get all available layers
  const { data: availableLayers } = useQuery(["layers"], async () => {
    const response = await GET(`/layers`);
    return response;
  });

  // get the lambda
  useQuery(
    ["lambda", lambdaId],
    async () => {
      if (!lambdaId) return undefined;
      const response = await GET(`/lambdas/${lambdaId}`);
      return response;
    },
    {
      enabled: !!lambdaId && !lambda,
      onSuccess: (data) => {
        if (data) setLambda(data);
      },
    }
  );

  // save the lambda (if we are creating one)
  const { mutate: createLambdaMutation, isError: isCreateLambdaError } =
    useMutation((formData) => POST("/lambdas", formData), {
      onSuccess: (data) => {
        navigate(`/lambdas`);
      },
      onError: (error) => {
        setShowError(true);
        setErrorText(error.message);
      },
    });

  // update the lambda (if we are editing one)
  const { mutate: updateLambdaMutation, isError: isUpdateLambdaError } =
    useMutation((formData) => PUT(`/lambdas/${lambdaId}`, formData), {
      onSuccess: () => {
        navigate(-1);
      },
      onError: (error) => {
        setShowError(true);
        setErrorText(error.message);
      },
    });

  // delete the lambda
  const { mutate: deleteLambdaMutation } = useMutation(
    () => DELETE(`/lambdas/${lambdaId}`),
    {
      onSuccess: () => {
        navigate("/lambdas");
      },
      onError: (error) => {
        setShowError(true);
        setErrorText(`Failed to delete lambda: ${error.message}`);
      },
    }
  );

  // format the lambda code
  const { mutate: formatCodeMutation, isLoading: isFormattingCode } =
    useMutation((code) => POST("/lambdas/format", { code }), {
      onSuccess: (formattedCode) => {
        setCode(formattedCode);
      },
      onError: (error) => {
        setShowError(true);
        setErrorText(`Failed to format code: ${error.message}`);
      },
    });

  // save the lambda
  const handleSave = async () => {
    const formData = {
      funcName,
      config,
      description: config.description,
      displayTitle,
      code,
      layers,
    };

    if (lambdaId) {
      updateLambdaMutation(formData);
    } else {
      createLambdaMutation(formData);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDeleteLambda = () => {
    if (window.confirm("Are you sure you want to delete this lambda?")) {
      deleteLambdaMutation();
    }
  };

  const handleUpdateLayer = () => {
    const layerName = "RectangleCoreDependencyLayer";
    const latestLayer = availableLayers.find(
      (layer) => layer.LayerName === layerName
    );
    if (latestLayer) {
      const latestArn = latestLayer.LatestMatchingVersion.LayerVersionArn;
      setLayers((prevLayers) =>
        prevLayers.map((arn) => (arn.includes(layerName) ? latestArn : arn))
      );
    }
  };

  // once we get the lambda, pre-populate the input fields
  useEffect(() => {
    if (lambda) {
      setFuncName(lambda.funcName || "");
      setConfig({
        description: lambda.config?.description || "",
      });
      setDisplayTitle(lambda.displayTitle || "");
      setCode(lambda.code || null, null, 2);
      setLayers(lambda.layers || []);
    } else if (availableLayers) {
      const rectangleCoreLayer = availableLayers.find(
        (layer) => layer.LayerName === "rectangle-core"
      );
      const newRelicLayer = availableLayers.find(
        (layer) => layer.LayerName === "NewRelicNodeJS18X"
      );

      const defaultLayers = [
        rectangleCoreLayer?.LatestMatchingVersion.LayerVersionArn,
        "arn:aws:lambda:us-east-2:451483290750:layer:NewRelicNodeJS18X:84",
      ].filter(Boolean);

      setLayers(defaultLayers);
    }
  }, [lambda, availableLayers]);

  return (
    <>
      <div className="space-y-2">
        {/* NAV AREA */}

        {/* EDIT AREA */}
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-12 lg:px-12">
          <h1
            className={`text-2xl font-bold text-gray-900 ${
              lambdaId ? "mb-2" : "mb-1"
            }`}
          >
            {lambdaId ? "Edit Lambda" : "Create Lambda"}
          </h1>
          <p className="text-sm mb-6 font-slate-700">
            This UI will accept arbitrary code and wrap it into a compatible
            Lambda layer. On the back-end Claude will format the code, and the
            prompts are aware of the available dependencies.
          </p>

          {/* Display Title */}
          <div>
            <label
              htmlFor="displayTitle"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Display Title
            </label>
            <div className="mt-1 mb-8">
              <input
                id="displayTitle"
                name="displayTitle"
                type="text"
                value={displayTitle}
                onChange={(e) => setDisplayTitle(e.target.value)}
                placeholder="Display title"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Description
            </label>
            <div className="mt-1 mb-8">
              <textarea
                id="description"
                name="description"
                value={config.description}
                onChange={(e) =>
                  setConfig({ ...config, description: e.target.value })
                }
                placeholder="Lambda description"
                rows={3}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          {/* Layers */}
          <div className="mb-6">
            <label
              htmlFor="layers"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Layers
            </label>
            <div className="mt-2 space-y-2">
              {layers.map((layerArn, index) => {
                const layerName = layerArn.split(":").slice(-2, -1)[0];
                const layerInfo = availableLayers?.find(
                  (layer) => layer.LayerName === layerName
                );
                const currentVersion = layerArn.split(":").pop();
                const latestVersion = layerInfo?.LatestMatchingVersion.Version;
                const isLatest = currentVersion === latestVersion?.toString();
                const isRefreshable =
                  layerName === "rectangle-core" &&
                  currentVersion !== latestVersion;

                return (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-slate-50 py-2 px-4 rounded-md text-sm text-gray-600 mb-1"
                  >
                    <div>
                      <p className="text-slate-800 font-medium">{layerName} </p>
                      <p>{layerArn}</p>
                    </div>
                    <div className="flex items-center">
                      {!isLatest && isRefreshable && (
                        <button
                          onClick={handleUpdateLayer}
                          className="text-blue-500 hover:text-blue-700 mr-2"
                          title="Update to latest version"
                        >
                          <RefreshCw size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Function Name */}
          <div>
            <label
              htmlFor="funcName"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Function Name
            </label>
            <div className="mt-1 mb-8">
              <input
                id="funcName"
                name="funcName"
                type="text"
                disabled={true}
                value={funcName}
                onChange={(e) => setFuncName(e.target.value)}
                placeholder="Function name (auto-generated)"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          {/* Code */}
          <div className="mt-4 mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-sm font-semibold">Lambda Function Code</h1>
              <p className="text-xs text-slate-700">
                Write your lambda function code here
              </p>
            </div>
            <Button
              label="Format into Lambda code"
              onClick={() => formatCodeMutation(code)}
              style={{ variant: "filled" }}
              rounded="md"
              disabled={isFormattingCode}
            />
          </div>
          <JSONEditor
            value={code}
            language={"javascript"}
            onChange={setCode}
            height="500"
          />

          {/* Save, Cancel, and Delete buttons */}
          <div className="flex items-center justify-between pb-8 mt-4">
            <Button
              label="Delete Lambda"
              onClick={handleDeleteLambda}
              style={{ variant: "filled" }}
              rounded="md"
              variant="danger"
              disabled={!lambdaId}
            />
            <div className="flex items-center gap-x-3">
              <Button
                label="Go back"
                onClick={handleGoBack}
                style={{ variant: "filled" }}
                rounded="md"
                variant="secondary"
              />
              <Button
                label="Save lambda"
                onClick={handleSave}
                style={{ variant: "filled" }}
                rounded="md"
                disabled={
                  createLambdaMutation.isLoading ||
                  updateLambdaMutation.isLoading
                }
              />
            </div>
          </div>
        </div>
      </div>

      <SimpleModal
        titleText={"There was an error"}
        bodyText={`Received this from the server: ${
          isCreateLambdaError || isUpdateLambdaError
            ? errorText
            : "unknown error"
        }`}
        variant={"error"}
        open={showError}
        setOpen={setShowError}
      />
    </>
  );
}
